import React, { useMemo, useState, useEffect } from 'react';
import './FlashlightOverlay.css';

// --- CONFIGURATION ---
// Adjust these to change the look!
// 1.5 = Radius is 1.5x the tile size. (Try 1.2 for smaller, 1.8 for larger)
const LIGHT_RADIUS_MULTIPLIER = 1.8; 

// 0% - 30% is purely clear, 30% - 100% fades to black.
// Reducing this makes the light look "softer" and effectively smaller.
const GRADIENT_CORE_SIZE = "25%"; 

const FlashlightOverlay = ({ 
    active, 
    playerPos, 
    goalPos, 
    tileSize, 
    gapSize,
    gridSize,
    offset = { x: 0, y: 0 } 
}) => {
    // 1. Generate a Unique ID for this specific instance to prevent caching/stale conflicts
    // We use a random string to ensure the mask/gradient IDs are globally unique.
    const uniqueId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
    const maskId = `light-mask-${uniqueId}`;
    const gradientId = `flashlight-gradient-${uniqueId}`;

    const [shouldRender, setShouldRender] = useState(active);

    useEffect(() => {
        let timeout;
        if (active) {
            setShouldRender(true);
        } else {
            timeout = setTimeout(() => {
                setShouldRender(false);
            }, 1000); // Wait for CSS fade out
        }
        return () => clearTimeout(timeout);
    }, [active]);

    const { playerX, playerY, goalX, goalY, radius } = useMemo(() => {
        if (!shouldRender) return {};

        const getCenter = (pos) => {
            return pos * (tileSize + gapSize) + (tileSize / 2);
        };

        return {
            playerX: getCenter(playerPos.x) + offset.x,
            playerY: getCenter(playerPos.y) + offset.y,
            goalX: (goalPos.x >= 0 ? getCenter(goalPos.x) : -5000) + offset.x,
            goalY: (goalPos.y >= 0 ? getCenter(goalPos.y) : -5000) + offset.y,
            radius: tileSize * LIGHT_RADIUS_MULTIPLIER 
        };
    }, [playerPos, goalPos, tileSize, gapSize, offset, shouldRender]);

    if (!shouldRender) return <div className="flashlight-layer" />;

    return (
        <div className={`flashlight-layer ${active ? 'active' : ''}`}>
            <svg width="100%" height="100%">
                <defs>
                    {/* SCOPED ID: Prevents using stale definitions from previous renders */}
                    <radialGradient id={gradientId}>
                        <stop offset="0%" stopColor="black" />
                        <stop offset={GRADIENT_CORE_SIZE} stopColor="black" /> 
                        <stop offset="100%" stopColor="white" /> 
                    </radialGradient>

                    <mask id={maskId}>
                        {/* Base: White = Opaque (Darkness) */}
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        
                        {/* Hole: Black = Transparent (Light) 
                            mixBlendMode: 'multiply' ensures lights merge correctly 
                        */}
                        <circle 
                            cx={playerX} 
                            cy={playerY} 
                            r={radius} 
                            fill={`url(#${gradientId})`} 
                            style={{ 
                                transition: 'cx 0.1s linear, cy 0.1s linear',
                                mixBlendMode: 'multiply' 
                            }}
                        />
                        
                        <circle 
                            cx={goalX} 
                            cy={goalY} 
                            r={radius * 0.8} 
                            fill={`url(#${gradientId})`} 
                            style={{ mixBlendMode: 'multiply' }} 
                        />
                    </mask>
                </defs>

                {/* The Overlay Rect using the Unique Mask ID */}
                <rect 
                    x="0" y="0" 
                    width="100%" height="100%" 
                    fill="black" 
                    mask={`url(#${maskId})`} 
                />
            </svg>
        </div>
    );
};

// Optimized Comparator
export default React.memo(FlashlightOverlay, (prevProps, nextProps) => {
    if (!prevProps.active && !nextProps.active) return true;
    
    return (
        prevProps.active === nextProps.active &&
        prevProps.playerPos === nextProps.playerPos && 
        prevProps.goalPos === nextProps.goalPos &&
        prevProps.tileSize === nextProps.tileSize &&
        prevProps.gapSize === nextProps.gapSize &&
        prevProps.gridSize === nextProps.gridSize &&
        prevProps.offset === nextProps.offset
    );
});